import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useFiliale } from '../context/FilialeContext';
import { useAuth } from '../context/AuthContext';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import {
  ChevronRight, ChevronLeft, Check, Download, Mail, ArrowLeft, CheckCircle2,
  Loader2, Eye, X, Search, Calendar, Briefcase, Users, FileText, Send,
  AlertTriangle, RotateCcw, ZoomIn, ZoomOut, Maximize2, Minimize2, ExternalLink, ArrowLeftRight,
} from 'lucide-react';

const NAVY = '#0F1E3D';
const GOLD = '#E3A81C';

const STEPS = [
  { label: 'Période', icon: Calendar },
  { label: 'Activité', icon: Briefcase },
  { label: 'Type', icon: Users },
  { label: 'Personnel', icon: FileText },
  { label: 'Action', icon: Send },
];

// Animations (injectées une seule fois, aucune config Tailwind nécessaire)
const CSS = `
@keyframes fp-up{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
@keyframes fp-fade{from{opacity:0}to{opacity:1}}
@keyframes fp-in-right{from{transform:translateX(100%)}to{transform:none}}
@keyframes fp-out-right{from{transform:none}to{transform:translateX(100%)}}
@keyframes fp-fade-out{from{opacity:1}to{opacity:0}}
@keyframes fp-pop{0%{transform:scale(.3);opacity:0}70%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
@keyframes fp-shimmer{from{background-position:-500px 0}to{background-position:500px 0}}
@keyframes fp-fall{0%{transform:translateY(-20px) rotate(0);opacity:1}100%{transform:translateY(320px) rotate(600deg);opacity:0}}
@keyframes fp-pulse{0%{box-shadow:0 0 0 0 rgba(227,168,28,.55)}100%{box-shadow:0 0 0 16px rgba(227,168,28,0)}}
@keyframes fp-stripes{from{background-position:0 0}to{background-position:40px 0}}
.fp-up{animation:fp-up .45s cubic-bezier(.2,.8,.2,1) both}
.fp-fade{animation:fp-fade .25s ease both}
.fp-fade-out{animation:fp-fade-out .25s ease both}
.fp-drawer{animation:fp-in-right .38s cubic-bezier(.22,1,.36,1) both}
.fp-drawer-out{animation:fp-out-right .28s ease-in both}
.fp-pop{animation:fp-pop .55s cubic-bezier(.2,.9,.3,1.2) both}
.fp-pulse{animation:fp-pulse 1.6s ease-out infinite}
.fp-skel{background:linear-gradient(90deg,#eef0f4 25%,#f8f9fb 50%,#eef0f4 75%);background-size:500px 100%;animation:fp-shimmer 1.3s linear infinite}
.fp-stripes{background-image:linear-gradient(135deg,rgba(255,255,255,.3) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.3) 50%,rgba(255,255,255,.3) 75%,transparent 75%);background-size:40px 40px;animation:fp-stripes .8s linear infinite}
@media (prefers-reduced-motion:reduce){.fp-up,.fp-fade,.fp-drawer,.fp-drawer-out,.fp-pop,.fp-pulse,.fp-skel,.fp-stripes{animation-duration:.01ms!important;animation-iteration-count:1!important}}
`;

const initials = (p) =>
  `${(p?.prenom || '')[0] || ''}${(p?.nom || '')[0] || ''}`.toUpperCase() || '?';
const fullName = (p) => `${p?.nom || ''} ${p?.prenom || ''}`.trim();
const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR');
const periodLabel = (p) =>
  p?.date_debut && p?.date_fin ? `${fmtDate(p.date_debut)} - ${fmtDate(p.date_fin)}` : 'Période non définie';
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Zoom de l'aperçu PDF : 'fit' = ajusté à la largeur, sinon pourcentage
const ZOOMS = [50, 75, 100, 125, 150, 200, 300];
const nextZoom = (z, dir) => {
  const cur = z === 'fit' ? 100 : z;
  if (dir > 0) return ZOOMS.find((v) => v > cur) ?? ZOOMS[ZOOMS.length - 1];
  return [...ZOOMS].reverse().find((v) => v < cur) ?? ZOOMS[0];
};
const zoomHash = (z) => (z === 'fit' ? 'zoom=page-width&view=FitH' : `zoom=${z}`);

/* ---------- Petits composants ---------- */

const Skeletons = ({ n = 4 }) => (
  <div className="space-y-3">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className="fp-skel h-[76px] rounded-xl" />
    ))}
  </div>
);

const SearchBox = ({ value, onChange, placeholder }) => (
  <div className="relative mb-5">
    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#E3A81C] focus:border-transparent transition"
    />
  </div>
);

const Empty = ({ text }) => (
  <div className="text-center text-gray-500 py-10 border-2 border-dashed border-gray-200 rounded-xl">{text}</div>
);

const StepShell = ({ title, subtitle, onBack, children }) => (
  <div className="bg-white rounded-2xl shadow-lg shadow-[#0F1E3D]/5 border border-gray-100 overflow-hidden fp-up">
    <div className="h-1.5 bg-gradient-to-r from-[#0F1E3D] via-[#1d3566] to-[#E3A81C]" />
    <div className="p-6 md:p-8">
      {onBack && (
        <button
          onClick={onBack}
          className="mb-4 -ml-2 px-2 py-1 flex items-center gap-2 text-sm text-gray-500 hover:text-[#0F1E3D] rounded-lg hover:bg-gray-100 transition"
        >
          <ArrowLeft size={18} /> Retour
        </button>
      )}
      <h2 className="text-2xl font-bold text-[#0F1E3D]">{title}</h2>
      {subtitle && <p className="text-gray-500 mt-1 mb-6">{subtitle}</p>}
      {!subtitle && <div className="mb-6" />}
      {children}
    </div>
  </div>
);

const ChoiceRow = ({ icon: Icon = CheckCircle2, title, subtitle, badge, onClick, index = 0 }) => (
  <button
    onClick={onClick}
    style={{ animationDelay: `${Math.min(index, 10) * 45}ms` }}
    className="fp-up group w-full flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-white text-left hover:border-[#E3A81C] hover:shadow-md hover:shadow-[#E3A81C]/10 hover:-translate-y-0.5 transition-all"
  >
    <div className="flex items-center gap-4 min-w-0">
      <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-[#0F1E3D] to-[#274380] flex items-center justify-center group-hover:from-[#E3A81C] group-hover:to-[#EDB52E] transition-colors">
        <Icon className="text-white group-hover:text-[#0F1E3D] transition-colors" size={22} />
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-[#0F1E3D] truncate flex items-center gap-2">
          {title}
          {badge && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E3A81C]/15 text-[#8a6300]">{badge}</span>
          )}
        </div>
        {subtitle && <div className="text-sm text-gray-500 truncate">{subtitle}</div>}
      </div>
    </div>
    <ChevronRight className="text-gray-300 group-hover:text-[#E3A81C] group-hover:translate-x-1 transition-all shrink-0" size={22} />
  </button>
);

const Avatar = ({ p, size = 44, active }) => (
  <div
    style={{ width: size, height: size }}
    className={`shrink-0 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
      active ? 'bg-[#E3A81C] text-[#0F1E3D]' : 'bg-[#0F1E3D] text-white'
    }`}
  >
    {initials(p)}
  </div>
);

/* ---------- Récapitulatif latéral ---------- */

const Recap = ({ steps, personnelObjs, currentStep, onGo, onPreview }) => (
  <aside className="lg:sticky lg:top-6 bg-gradient-to-b from-[#0F1E3D] to-[#16295A] text-white rounded-2xl p-6 shadow-xl shadow-[#0F1E3D]/20">
    <div className="flex items-center gap-2 mb-5">
      <FileText size={18} className="text-[#E3A81C]" />
      <h3 className="font-semibold">Votre sélection</h3>
    </div>
    <ul className="space-y-4">
      {steps.map((s, i) => {
        const reached = i + 1 < currentStep && s.value;
        return (
          <li key={s.label}>
            <button
              disabled={!reached}
              onClick={() => onGo(i + 1)}
              className="w-full text-left flex gap-3 group disabled:cursor-default"
            >
              <div
                className={`mt-0.5 w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-xs ${
                  s.value ? 'bg-[#E3A81C] text-[#0F1E3D]' : 'border border-white/30 text-white/40'
                }`}
              >
                {s.value ? <Check size={14} /> : i + 1}
              </div>
              <div className="min-w-0">
                <div className="text-xs text-white/60">{s.label}</div>
                <div className={`text-sm truncate ${s.value ? 'font-medium' : 'text-white/35 italic'}`}>
                  {s.value || 'À choisir'}
                </div>
                {s.sub && <div className="text-xs text-white/50 truncate">{s.sub}</div>}
              </div>
            </button>
          </li>
        );
      })}
    </ul>

    {personnelObjs.length > 0 && (
      <div className="mt-6 pt-5 border-t border-white/10 fp-fade">
        <div className="flex -space-x-2 mb-3">
          {personnelObjs.slice(0, 5).map((p) => (
            <div key={p.idpersonnel} className="ring-2 ring-[#16295A] rounded-full">
              <Avatar p={p} size={32} active />
            </div>
          ))}
          {personnelObjs.length > 5 && (
            <div className="w-8 h-8 rounded-full bg-white/15 ring-2 ring-[#16295A] text-xs flex items-center justify-center">
              +{personnelObjs.length - 5}
            </div>
          )}
        </div>
        <button
          onClick={() => onPreview(personnelObjs, 0)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition"
        >
          <Eye size={16} /> Aperçu des bulletins
        </button>
      </div>
    )}
  </aside>
);

/* ---------- Tiroir d'aperçu du bulletin ---------- */

const PreviewDrawer = ({ preview, closing, onClose, onNav, url, loading, error, onRetry, onDownload, paie, zoom, onZoom, expanded, onToggleExpand }) => {
  if (!preview) return null;
  const { list, index } = preview;
  const p = list[index];
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Aperçu du bulletin">
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-[#0F1E3D]/60 backdrop-blur-sm ${closing ? 'fp-fade-out' : 'fp-fade'}`}
      />
      <div className={`relative w-full ${expanded ? 'max-w-6xl' : 'max-w-3xl'} transition-[max-width] duration-300 h-full bg-gray-100 flex flex-col shadow-2xl ${closing ? 'fp-drawer-out' : 'fp-drawer'}`}>
        <div className="bg-gradient-to-r from-[#0F1E3D] to-[#274380] text-white px-6 py-4 flex items-center gap-4">
          <Avatar p={p} active />
          <div className="min-w-0 flex-1">
            <div className="font-semibold truncate">{fullName(p)}</div>
            <div className="text-sm text-white/70 truncate">
              {p.matricule} · {paie?.libelle_paie}
            </div>
          </div>
          <button onClick={onClose} aria-label="Fermer" className="p-2 rounded-lg hover:bg-white/15 transition">
            <X size={22} />
          </button>
        </div>

        <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              onClick={() => onZoom(nextZoom(zoom, -1))}
              aria-label="Réduire"
              title="Réduire (touche -)"
              className="p-2 rounded-lg hover:bg-gray-100 text-[#0F1E3D] transition"
            >
              <ZoomOut size={18} />
            </button>
            <button
              onClick={() => onZoom(100)}
              title="Taille réelle (touche 0)"
              className="min-w-[68px] px-2 py-1.5 text-sm font-medium tabular-nums rounded-lg bg-gray-100 hover:bg-gray-200 text-[#0F1E3D] transition"
            >
              {zoom === 'fit' ? 'Auto' : `${zoom}%`}
            </button>
            <button
              onClick={() => onZoom(nextZoom(zoom, 1))}
              aria-label="Agrandir"
              title="Agrandir (touche +)"
              className="p-2 rounded-lg hover:bg-gray-100 text-[#0F1E3D] transition"
            >
              <ZoomIn size={18} />
            </button>
            <button
              onClick={() => onZoom('fit')}
              title="Ajuster à la largeur"
              className={`ml-1 flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition ${
                zoom === 'fit' ? 'bg-[#E3A81C]/20 text-[#0F1E3D] font-medium' : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <ArrowLeftRight size={16} /> <span className="hidden sm:inline">Ajuster</span>
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onToggleExpand}
              title={expanded ? 'Réduire le panneau' : 'Agrandir le panneau'}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg hover:bg-gray-100 text-gray-600 transition"
            >
              {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              {expanded ? 'Réduire' : 'Élargir'}
            </button>
            <button
              onClick={() => url && window.open(url, '_blank')}
              disabled={!url}
              title="Ouvrir dans un nouvel onglet"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg hover:bg-gray-100 text-gray-600 transition disabled:opacity-40"
            >
              <ExternalLink size={16} /> <span className="hidden sm:inline">Nouvel onglet</span>
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 md:p-6 min-h-0">
          <div className="h-full rounded-xl bg-white shadow-inner overflow-hidden relative border border-gray-200">
            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#0F1E3D] fp-skel">
                <Loader2 className="animate-spin" size={32} />
                <span className="text-sm font-medium">Génération du bulletin…</span>
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center p-6">
                <AlertTriangle className="text-red-500" size={36} />
                <div className="font-semibold text-[#0F1E3D]">Impossible d’afficher ce bulletin</div>
                <div className="text-sm text-gray-500">Vérifiez que la paie est calculée pour cet employé, puis réessayez.</div>
                <button onClick={onRetry} className="mt-2 flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F1E3D] text-white text-sm">
                  <RotateCcw size={16} /> Réessayer
                </button>
              </div>
            )}
            {url && !loading && (
              <iframe key={`${url}|${zoom}`} title="Bulletin de paie" src={`${url}#${zoomHash(zoom)}&navpanes=0`} className="w-full h-full fp-fade" />
            )}
          </div>
        </div>

        <div className="bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onNav(-1)}
              disabled={index === 0}
              aria-label="Bulletin précédent"
              className="p-2 rounded-lg border border-gray-200 hover:border-[#E3A81C] disabled:opacity-30 disabled:hover:border-gray-200 transition"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm text-gray-600 tabular-nums min-w-[64px] text-center">
              {index + 1} / {list.length}
            </span>
            <button
              onClick={() => onNav(1)}
              disabled={index === list.length - 1}
              aria-label="Bulletin suivant"
              className="p-2 rounded-lg border border-gray-200 hover:border-[#E3A81C] disabled:opacity-30 disabled:hover:border-gray-200 transition"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <button
            onClick={() => onDownload(p)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#E3A81C] text-[#0F1E3D] font-medium hover:bg-[#EDB52E] transition"
          >
            <Download size={18} /> Télécharger ce bulletin
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Écran de succès ---------- */

const CONFETTI = Array.from({ length: 28 }, (_, i) => ({
  left: (i * 37) % 100,
  delay: (i % 7) * 0.12,
  dur: 1.6 + (i % 5) * 0.25,
  color: [GOLD, NAVY, '#4ade80', '#60a5fa'][i % 4],
}));

const Success = ({ action, count, onReset }) => (
  <div className="relative bg-white rounded-2xl shadow-lg border border-gray-100 p-10 text-center overflow-hidden fp-up">
    <div className="absolute inset-x-0 top-0 h-0 pointer-events-none" aria-hidden="true">
      {CONFETTI.map((c, i) => (
        <span
          key={i}
          style={{
            left: `${c.left}%`, background: c.color,
            animation: `fp-fall ${c.dur}s ${c.delay}s ease-in both`,
          }}
          className="absolute w-2 h-3 rounded-sm"
        />
      ))}
    </div>
    <div className="fp-pop fp-pulse w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mx-auto mb-5">
      <Check className="text-white" size={48} strokeWidth={3} />
    </div>
    <h2 className="text-2xl font-bold text-[#0F1E3D] mb-2">
      {action === 'download' ? 'Bulletins téléchargés' : 'Bulletins envoyés'}
    </h2>
    <p className="text-gray-600 mb-8">
      {action === 'download'
        ? `${count} bulletin(s) enregistré(s) sur votre appareil.`
        : `${count} bulletin(s) envoyé(s) par email.`}
    </p>
    <button
      onClick={onReset}
      className="inline-flex items-center gap-2 px-6 py-3 bg-[#E3A81C] text-[#0F1E3D] rounded-xl hover:bg-[#EDB52E] transition font-semibold shadow-md shadow-[#E3A81C]/30"
    >
      Nouvelle exportation <ChevronRight size={20} />
    </button>
  </div>
);

/* ---------- Page principale ---------- */

const ExportFichePaie = () => {
  const { filialeId, isConsolidated } = useFiliale();
  const { user } = useAuth(); // eslint-disable-line no-unused-vars

  const [currentStep, setCurrentStep] = useState(1);

  const [paies, setPaies] = useState([]);
  const [activites, setActivites] = useState([]);
  const [typesPersonnel, setTypesPersonnel] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loadingList, setLoadingList] = useState({ paies: false, activites: false, types: false, pers: false });

  const [searchPaie, setSearchPaie] = useState('');
  const [searchTypePersonnel, setSearchTypePersonnel] = useState('');
  const [searchPersonnel, setSearchPersonnel] = useState('');

  const [selectedPaie, setSelectedPaie] = useState(null);
  const [selectedActivite, setSelectedActivite] = useState(null);
  const [selectedTypePersonnel, setSelectedTypePersonnel] = useState(null);
  const [selectedPersonnel, setSelectedPersonnel] = useState([]);

  const [selectedAction, setSelectedAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Aperçu
  const [preview, setPreview] = useState(null); // { list, index }
  const [closing, setClosing] = useState(false);
  const [prevUrl, setPrevUrl] = useState(null);
  const [prevLoading, setPrevLoading] = useState(false);
  const [prevError, setPrevError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [zoom, setZoom] = useState('fit');
  const [expanded, setExpanded] = useState(false);

  const setFlag = (k, v) => setLoadingList((s) => ({ ...s, [k]: v }));
  const withFiliale = (params) => {
    if (!isConsolidated && filialeId) params.filiale_id = filialeId;
    return params;
  };

  /* --- Chargements --- */
  const fetchPaies = async () => {
    setFlag('paies', true);
    try {
      const response = await http.get('/api/paies', { params: withFiliale({}) });
      setPaies(response.data || []);
    } catch (error) {
      console.error('Erreur chargement paies:', error);
    } finally {
      setFlag('paies', false);
    }
  };

  const fetchActivites = async () => {
    setFlag('activites', true);
    try {
      const response = await http.get('/api/activites');
      setActivites(response.data || []);
    } catch (error) {
      console.error('Erreur chargement activités:', error);
    } finally {
      setFlag('activites', false);
    }
  };

  const fetchTypesPersonnel = async (activiteId) => {
    setFlag('types', true);
    setTypesPersonnel([]);
    try {
      const response = await http.get('/api/types-personnel', { params: withFiliale({ idactivite: activiteId }) });
      setTypesPersonnel(response.data || []);
    } catch (error) {
      console.error('Erreur chargement types personnel:', error);
    } finally {
      setFlag('types', false);
    }
  };

  const fetchPersonnel = async (typePersonnelId) => {
    setFlag('pers', true);
    setPersonnel([]);
    try {
      const response = await http.get('/api/personnel', { params: withFiliale({ type_personnel_id: typePersonnelId }) });
      // Réponse paginée : {data: [...], total, page, limit}
      setPersonnel(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('[ExportFichePaie] Erreur chargement personnel:', error);
      setPersonnel([]);
    } finally {
      setFlag('pers', false);
    }
  };

  useEffect(() => {
    fetchPaies();
    fetchActivites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filialeId, isConsolidated]);

  /* --- Navigation : reculer ne supprime plus la sélection, avancer réinitialise l'aval --- */
  const handleSelectPaie = (paie) => {
    setSelectedPaie({ ...paie, isInterim: paie.idpaie_interime !== undefined });
    setSelectedActivite(null);
    setSelectedTypePersonnel(null);
    setSelectedPersonnel([]);
    setSelectedAction(null);
    setCurrentStep(2);
  };

  const handleSelectActivite = (activite) => {
    setSelectedActivite(activite);
    setSelectedTypePersonnel(null);
    setSelectedPersonnel([]);
    setSelectedAction(null);
    fetchTypesPersonnel(activite.idactivites);
    setCurrentStep(3);
  };

  const handleSelectTypePersonnel = (type) => {
    setSelectedTypePersonnel(type);
    setSelectedPersonnel([]);
    setSelectedAction(null);
    setSearchPersonnel('');
    fetchPersonnel(type.idtype_personnel);
    setCurrentStep(4);
  };

  const goBackTo = (step) => {
    if (loading) return;
    setErrorMsg('');
    setCurrentStep(step);
  };

  /* --- Sélection du personnel --- */
  const filteredPersonnel = useMemo(() => {
    const q = searchPersonnel.toLowerCase();
    return personnel.filter(
      (p) =>
        !q ||
        (p.nom || '').toLowerCase().includes(q) ||
        (p.prenom || '').toLowerCase().includes(q) ||
        (p.matricule || '').toLowerCase().includes(q)
    );
  }, [personnel, searchPersonnel]);

  const allFilteredSelected =
    filteredPersonnel.length > 0 && filteredPersonnel.every((p) => selectedPersonnel.includes(p.idpersonnel));

  const handleTogglePersonnel = (id) =>
    setSelectedPersonnel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSelectAll = () => {
    const ids = filteredPersonnel.map((p) => p.idpersonnel);
    setSelectedPersonnel((prev) =>
      allFilteredSelected ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]))
    );
  };

  const personnelObjs = useMemo(
    () => personnel.filter((p) => selectedPersonnel.includes(p.idpersonnel)),
    [personnel, selectedPersonnel]
  );

  /* --- Bulletins --- */
  const bulletinParams = useCallback(
    (persId) => {
      // Déterminer la table d'éléments de paie (table datée ou standard)
      let tableElement = selectedPaie?.isInterim ? 'paie_element_interim' : 'paie_element';
      const dateDebut = selectedPaie?.date_debut;
      if (dateDebut) {
        const d = new Date(dateDebut);
        if (!isNaN(d)) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          tableElement = `${tableElement}_${yyyy}_${mm}_${dd}`;
        }
      }

      return {
        paie_id: selectedPaie?.idpaie || selectedPaie?.idpaie_interime,
        personnel_id: persId,
        is_interim: selectedPaie?.isInterim ? 'true' : 'false',
        tableElement,
      };
    },
    [selectedPaie]
  );

  const saveBlob = (blob, name) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const downloadOne = async (p) => {
    try {
      const response = await http.get('/api/bulletins/pdf', { params: bulletinParams(p.idpersonnel), responseType: 'blob' });
      saveBlob(response.data, `bulletin_${p.matricule || p.idpersonnel}.pdf`);
    } catch (e) {
      console.error('Erreur téléchargement bulletin:', e);
      setPrevError(true);
    }
  };

  const handleAction = async () => {
    if (!selectedAction) return;
    setLoading(true);
    setErrorMsg('');
    setProgress({ done: 0, total: selectedPersonnel.length });
    try {
      if (selectedAction === 'download') {
        let done = 0;
        for (const persId of selectedPersonnel) {
          const p = personnel.find((x) => x.idpersonnel === persId);
          const response = await http.get('/api/bulletins/pdf', { params: bulletinParams(persId), responseType: 'blob' });
          saveBlob(response.data, `bulletin_${p?.matricule || persId}.pdf`);
          done += 1;
          setProgress({ done, total: selectedPersonnel.length });
          await sleep(250); // évite le blocage des téléchargements multiples par le navigateur
        }
      } else {
        await http.post('/api/bulletins/envoyer', {
          paie_id: selectedPaie.idpaie || selectedPaie.idpaie_interime,
          personnel_ids: selectedPersonnel,
          is_interim: selectedPaie.isInterim ? 'true' : 'false',
        });
      }
      setSuccess(true);
    } catch (error) {
      console.error('Erreur action:', error);
      setErrorMsg(
        selectedAction === 'download'
          ? 'Le téléchargement s’est interrompu. Relancez l’opération ou vérifiez votre connexion.'
          : 'L’envoi des emails a échoué. Vérifiez les adresses email du personnel, puis relancez.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSuccess(false);
    setSelectedPaie(null);
    setSelectedActivite(null);
    setSelectedTypePersonnel(null);
    setSelectedPersonnel([]);
    setSelectedAction(null);
    setErrorMsg('');
    setCurrentStep(1);
  };

  /* --- Tiroir d'aperçu --- */
  const openPreview = (list, index = 0) => {
    setClosing(false);
    setPreview({ list, index });
  };

  const closePreview = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setPreview(null);
      setClosing(false);
    }, 280);
  }, []);

  const navPreview = useCallback(
    (delta) =>
      setPreview((pv) => {
        if (!pv) return pv;
        const index = Math.min(Math.max(pv.index + delta, 0), pv.list.length - 1);
        return { ...pv, index };
      }),
    []
  );

  const currentPreviewId = preview ? preview.list[preview.index]?.idpersonnel : null;

  useEffect(() => {
    if (currentPreviewId == null) return undefined;
    let cancelled = false;
    let url = null;
    setPrevLoading(true);
    setPrevError(false);
    setPrevUrl(null);
    http
      .get('/api/bulletins/pdf', { params: bulletinParams(currentPreviewId), responseType: 'blob' })
      .then((r) => {
        url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        if (!cancelled) setPrevUrl(url);
      })
      .catch(() => !cancelled && setPrevError(true))
      .finally(() => !cancelled && setPrevLoading(false));
    return () => {
      cancelled = true;
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [currentPreviewId, retryKey, bulletinParams]);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closePreview();
      if (e.key === 'ArrowRight') navPreview(1);
      if (e.key === 'ArrowLeft') navPreview(-1);
      if (e.key === '+' || e.key === '=') setZoom((z) => nextZoom(z, 1));
      if (e.key === '-') setZoom((z) => nextZoom(z, -1));
      if (e.key === '0') setZoom(100);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, closePreview, navPreview]);

  /* --- Données du récapitulatif --- */
  const recapSteps = [
    { label: 'Période', value: selectedPaie?.libelle_paie, sub: selectedPaie ? periodLabel(selectedPaie) : null },
    { label: 'Activité', value: selectedActivite?.Libelle_activite },
    { label: 'Type de personnel', value: selectedTypePersonnel?.libelle },
    {
      label: 'Personnel',
      value: selectedPersonnel.length ? `${selectedPersonnel.length} employé(s)` : null,
    },
  ];

  const filteredPaies = paies.filter(
    (p) => !searchPaie || (p.libelle_paie || '').toLowerCase().includes(searchPaie.toLowerCase())
  );
  const filteredTypes = typesPersonnel.filter(
    (t) => !searchTypePersonnel || (t.libelle || '').toLowerCase().includes(searchTypePersonnel.toLowerCase())
  );

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-slate-100">
      <style>{CSS}</style>

      <BlueHeader
        title="Export et Envoi de Fiches de Paie"
        subtitle="Génération et distribution des bulletins de paie"
        icon={Mail}
        showBack={false}
      />

      <div className="px-4 md:px-6 py-8 w-full">
        {/* Progression */}
        <nav className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 px-4 md:px-8 py-5" aria-label="Progression">
          <div className="flex items-center">
            {STEPS.map((s, i) => {
              const step = i + 1;
              const done = success || currentStep > step;
              const active = !success && currentStep === step;
              const Icon = s.icon;
              return (
                <React.Fragment key={s.label}>
                  <button
                    disabled={!done || success || loading}
                    onClick={() => goBackTo(step)}
                    className="flex flex-col items-center gap-2 disabled:cursor-default"
                  >
                    <div
                      className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 ${
                        done
                          ? 'bg-[#0F1E3D] text-[#E3A81C]'
                          : active
                          ? 'bg-[#E3A81C] text-[#0F1E3D] scale-110 fp-pulse'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {done ? <Check size={20} strokeWidth={3} /> : <Icon size={20} />}
                    </div>
                    <span className={`text-xs hidden sm:block ${active ? 'font-semibold text-[#0F1E3D]' : 'text-gray-500'}`}>
                      {s.label}
                    </span>
                  </button>
                  {step < STEPS.length && (
                    <div className="flex-1 h-1 mx-2 sm:mb-6 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#0F1E3D] to-[#E3A81C] transition-all duration-700 ease-out"
                        style={{ width: done ? '100%' : '0%' }}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </nav>

        <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-start">
          <div key={success ? 'ok' : currentStep}>
            {/* Étape 1 */}
            {!success && currentStep === 1 && (
              <StepShell title="Sélectionnez la période de paie" subtitle="Choisissez le mois ou la période à exporter.">
                <SearchBox value={searchPaie} onChange={setSearchPaie} placeholder="Rechercher une période…" />
                {loadingList.paies ? (
                  <Skeletons />
                ) : filteredPaies.length === 0 ? (
                  <Empty text="Aucune période ne correspond à votre recherche." />
                ) : (
                  <div className="space-y-3">
                    {filteredPaies.map((paie, i) => (
                      <ChoiceRow
                        key={paie.idpaie || paie.idpaie_interime}
                        index={i}
                        icon={Calendar}
                        title={paie.libelle_paie}
                        subtitle={periodLabel(paie)}
                        badge={paie.idpaie_interime !== undefined ? 'Intérim' : null}
                        onClick={() => handleSelectPaie(paie)}
                      />
                    ))}
                  </div>
                )}
              </StepShell>
            )}

            {/* Étape 2 */}
            {!success && currentStep === 2 && (
              <StepShell title="Sélectionnez l’activité" onBack={() => goBackTo(1)}>
                {loadingList.activites ? (
                  <Skeletons />
                ) : activites.length === 0 ? (
                  <Empty text="Aucune activité disponible." />
                ) : (
                  <div className="space-y-3">
                    {activites.map((a, i) => (
                      <ChoiceRow
                        key={a.idactivites}
                        index={i}
                        icon={Briefcase}
                        title={a.Libelle_activite}
                        onClick={() => handleSelectActivite(a)}
                      />
                    ))}
                  </div>
                )}
              </StepShell>
            )}

            {/* Étape 3 */}
            {!success && currentStep === 3 && (
              <StepShell title="Sélectionnez le type de personnel" onBack={() => goBackTo(2)}>
                <SearchBox value={searchTypePersonnel} onChange={setSearchTypePersonnel} placeholder="Rechercher un type de personnel…" />
                {loadingList.types ? (
                  <Skeletons />
                ) : filteredTypes.length === 0 ? (
                  <Empty text="Aucun type de personnel pour cette activité." />
                ) : (
                  <div className="space-y-3">
                    {filteredTypes.map((t, i) => (
                      <ChoiceRow
                        key={t.idtype_personnel}
                        index={i}
                        icon={Users}
                        title={t.libelle}
                        onClick={() => handleSelectTypePersonnel(t)}
                      />
                    ))}
                  </div>
                )}
              </StepShell>
            )}

            {/* Étape 4 */}
            {!success && currentStep === 4 && (
              <StepShell
                title="Sélectionnez le personnel"
                subtitle="Cliquez sur l’œil pour voir le bulletin avant de l’exporter."
                onBack={() => goBackTo(3)}
              >
                <SearchBox value={searchPersonnel} onChange={setSearchPersonnel} placeholder="Nom, prénom ou matricule…" />

                <label className="flex items-center gap-3 mb-4 cursor-pointer select-none w-fit">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={handleSelectAll}
                    className="w-5 h-5 rounded border-gray-300 text-[#E3A81C] focus:ring-[#E3A81C]"
                  />
                  <span className="font-medium text-[#0F1E3D]">
                    Tout sélectionner{searchPersonnel ? ' (résultats filtrés)' : ''}
                  </span>
                </label>

                {loadingList.pers ? (
                  <Skeletons n={5} />
                ) : filteredPersonnel.length === 0 ? (
                  <Empty text="Aucun personnel trouvé pour ce type." />
                ) : (
                  <div className="space-y-2.5 max-h-[460px] overflow-y-auto pr-1">
                    {filteredPersonnel.map((pers, i) => {
                      const checked = selectedPersonnel.includes(pers.idpersonnel);
                      return (
                        <div
                          key={pers.idpersonnel}
                          role="checkbox"
                          aria-checked={checked}
                          tabIndex={0}
                          onClick={() => handleTogglePersonnel(pers.idpersonnel)}
                          onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && (e.preventDefault(), handleTogglePersonnel(pers.idpersonnel))}
                          style={{ animationDelay: `${Math.min(i, 10) * 30}ms` }}
                          className={`fp-up flex items-center gap-4 p-3.5 rounded-xl border cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-[#E3A81C] ${
                            checked
                              ? 'border-[#E3A81C] bg-[#E3A81C]/10 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-[#E3A81C]/60 hover:bg-gray-50'
                          }`}
                        >
                          <div
                            className={`w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center transition-all ${
                              checked ? 'bg-[#E3A81C] border-[#E3A81C]' : 'border-gray-300 bg-white'
                            }`}
                          >
                            {checked && <Check size={16} strokeWidth={3} className="text-[#0F1E3D] fp-pop" />}
                          </div>
                          <Avatar p={pers} active={checked} />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[#0F1E3D] truncate">{fullName(pers)}</div>
                            <div className="text-sm text-gray-500">{pers.matricule}</div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openPreview([pers], 0);
                            }}
                            aria-label={`Aperçu du bulletin de ${fullName(pers)}`}
                            className="p-2.5 rounded-lg text-[#0F1E3D] bg-gray-100 hover:bg-[#0F1E3D] hover:text-white transition"
                          >
                            <Eye size={18} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-500">
                    {selectedPersonnel.length} sur {personnel.length} sélectionné(s)
                  </span>
                  <button
                    onClick={() => setCurrentStep(5)}
                    disabled={selectedPersonnel.length === 0}
                    className="flex items-center gap-2 px-6 py-3 bg-[#E3A81C] text-[#0F1E3D] rounded-xl hover:bg-[#EDB52E] transition font-semibold shadow-md shadow-[#E3A81C]/30 disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed"
                  >
                    Continuer <ChevronRight size={20} />
                  </button>
                </div>
              </StepShell>
            )}

            {/* Étape 5 */}
            {!success && currentStep === 5 && (
              <StepShell
                title="Choisissez l’action"
                subtitle={`${selectedPersonnel.length} bulletin(s) pour « ${selectedPaie?.libelle_paie} ».`}
                onBack={loading ? null : () => goBackTo(4)}
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { id: 'download', icon: Download, title: 'Télécharger', text: 'Un fichier PDF par employé, enregistré sur votre appareil.' },
                    { id: 'email', icon: Mail, title: 'Envoyer par email', text: 'Chaque employé reçoit son bulletin dans sa boîte mail.' },
                  ].map((a) => {
                    const Icon = a.icon;
                    const on = selectedAction === a.id;
                    return (
                      <button
                        key={a.id}
                        disabled={loading}
                        onClick={() => setSelectedAction(a.id)}
                        aria-pressed={on}
                        className={`relative flex flex-col items-center gap-3 p-8 rounded-2xl border-2 text-center transition-all disabled:opacity-60 ${
                          on
                            ? 'border-[#E3A81C] bg-[#E3A81C]/10 shadow-lg shadow-[#E3A81C]/20 -translate-y-1'
                            : 'border-gray-200 hover:border-[#E3A81C]/60 hover:-translate-y-0.5'
                        }`}
                      >
                        {on && (
                          <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#E3A81C] flex items-center justify-center fp-pop">
                            <Check size={14} strokeWidth={3} className="text-[#0F1E3D]" />
                          </span>
                        )}
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors ${on ? 'bg-[#E3A81C]' : 'bg-[#0F1E3D]'}`}>
                          <Icon className={on ? 'text-[#0F1E3D]' : 'text-white'} size={30} />
                        </div>
                        <div className="font-semibold text-lg text-[#0F1E3D]">{a.title}</div>
                        <div className="text-sm text-gray-500">{a.text}</div>
                      </button>
                    );
                  })}
                </div>

                {errorMsg && (
                  <div className="mt-5 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 fp-up" role="alert">
                    <AlertTriangle size={20} className="shrink-0 mt-0.5" />
                    <span className="text-sm">{errorMsg}</span>
                  </div>
                )}

                {loading && (
                  <div className="mt-6 fp-fade">
                    <div className="flex justify-between text-sm text-[#0F1E3D] mb-2">
                      <span className="flex items-center gap-2">
                        <Loader2 className="animate-spin" size={16} />
                        {selectedAction === 'download' ? `Téléchargement ${progress.done} / ${progress.total}` : 'Envoi en cours…'}
                      </span>
                      {selectedAction === 'download' && <span className="tabular-nums">{pct}%</span>}
                    </div>
                    <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#E3A81C] fp-stripes transition-all duration-300"
                        style={{ width: selectedAction === 'download' ? `${Math.max(pct, 6)}%` : '100%' }}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-6 pt-5 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                  <button
                    onClick={() => openPreview(personnelObjs, 0)}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 text-[#0F1E3D] hover:border-[#0F1E3D] transition disabled:opacity-50"
                  >
                    <Eye size={18} /> Aperçu des bulletins
                  </button>
                  <button
                    onClick={handleAction}
                    disabled={!selectedAction || loading}
                    className="flex items-center gap-2 px-7 py-3 bg-[#E3A81C] text-[#0F1E3D] rounded-xl hover:bg-[#EDB52E] transition font-semibold shadow-md shadow-[#E3A81C]/30 disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed"
                  >
                    {selectedAction === 'email' ? <Send size={18} /> : <Download size={18} />}
                    {selectedAction === 'email' ? 'Envoyer les bulletins' : 'Télécharger les bulletins'}
                  </button>
                </div>
              </StepShell>
            )}

            {success && <Success action={selectedAction} count={selectedPersonnel.length} onReset={handleReset} />}
          </div>

          {!success && (
            <Recap
              steps={recapSteps}
              personnelObjs={personnelObjs}
              currentStep={currentStep}
              onGo={goBackTo}
              onPreview={openPreview}
            />
          )}
        </div>
      </div>

      <PreviewDrawer
        preview={preview}
        closing={closing}
        onClose={closePreview}
        onNav={navPreview}
        url={prevUrl}
        loading={prevLoading}
        error={prevError}
        onRetry={() => setRetryKey((k) => k + 1)}
        onDownload={downloadOne}
        zoom={zoom}
        onZoom={setZoom}
        expanded={expanded}
        onToggleExpand={() => setExpanded((v) => !v)}
        paie={selectedPaie}
      />
    </div>
  );
};

export default ExportFichePaie;